/**
 * @fileoverview Dataset Builder wizard — 9-step creation/edit flow for migration dataset templates.
 * @module system-web/components/migration/DatasetBuilderWizard
 *
 * Responsibilities:
 * - Walk the admin through all 9 wizard steps (§3.11.9): Name & Purpose, Primary Source,
 *   Child Tables & Joins, Source Filters, AI Column Enrichment (Pass 1), Target Collections,
 *   Field Mapping (with Pass 2 AI mapping suggestions), Dependency Declaration, Validation Summary.
 * - Load enabled schema manifest tables from Dexie (liveQuery-safe read) for source table pickers.
 * - Validate each step before advancing; block advance on errors.
 * - Save the template as draft on any exit; activate only on Step 9 when all guards pass.
 * - When `editId` is provided, load the existing template from Dexie and populate wizard state.
 *
 * Dependencies:
 * - useDatasetTemplates for create/update/activate mutations.
 * - legacySchemaManifestService.getSchemaManifest for schema table discovery.
 * - KNOWN_COLLECTIONS from @firesquirrel/legacy-migration for target collection pickers.
 * - FieldMappingEditor component for step 7.
 * - lucide-react icons.
 *
 * Architecture:
 * - Local-first: all step state held in React useState; mutations POST to server via useDatasetTemplates.
 * - Schema manifest tables loaded from Dexie on mount (non-liveQuery — wizard is ephemeral).
 * - KNOWN_COLLECTIONS is static — no Dexie read required for target collection list.
 * - No direct API calls.
 *
 * Notes:
 * - AI Column Enrichment step (step 5) and Field Mapping AI toolbar are skipped when
 *   VITE_AI_ANNOTATION_ENABLED !== 'true'.
 * - "Save as Draft" is always available on every step.
 * - Rendered as a full-screen modal overlay over the DatasetTemplateGrid.
 *
 * @localFirst
 * Source of Truth: server Mongo (server-authoritative).
 * Write Flow: wizard calls create() or update() on save; activate() on Step 9 activate.
 * Sync Strategy: Pull-based after server write.
 * Conflict Resolution: Server wins; 409/422 errors surfaced as inline error banners.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ArrowRight, Save, CheckCircle2, AlertTriangle,
  Loader2, X, Plus, Trash2,
} from 'lucide-react';
import {
  KNOWN_COLLECTIONS,
  REGISTERED_ADAPTERS_MANIFEST,
} from '../../types/legacyMigrationConstants';
import type { KnownCollectionEntry } from '../../types/legacyMigrationTypes';
import type {
  MigrationDatasetTemplate,
  ColumnProfile,
  SuggestedMapping,
  FieldMapping,
  SqlFilter,
  SqlRelationship,
} from '../../types/legacyMigrationTypes';
import { getSchemaManifest, type SchemaManifestTableEntry, type SchemaManifestColumn } from '../../services/legacySchemaManifestService';
import { getDatasetTemplate, enrichDatasetTemplate, matchDatasetTemplate } from '../../services/datasetTemplateService';
import { useDatasetTemplates } from '../../hooks/useDatasetTemplates';
import { FieldMappingEditor, type SourceTableColumns, type TargetCollectionFields } from './FieldMappingEditor';

// ---------------------------------------------------------------------------
// Helper: extract plain string column names from SchemaManifestColumn[] or string[]
// ---------------------------------------------------------------------------
function toColumnNames(cols: SchemaManifestColumn[] | string[] | undefined): string[] {
  if (!cols) return [];
  if (cols.length === 0) return [];
  if (typeof cols[0] === 'string') return cols as string[];
  return (cols as SchemaManifestColumn[]).map((c) => c.column);
}

// ---------------------------------------------------------------------------
// Feature flag
// ---------------------------------------------------------------------------

const AI_SAMPLING_ENABLED = import.meta.env.VITE_AI_ANNOTATION_ENABLED === 'true';

// ---------------------------------------------------------------------------
// Wizard step definitions
// ---------------------------------------------------------------------------

const STEPS = [
  { id: 1, label: 'Name & Purpose' },
  { id: 2, label: 'Source Table' },
  { id: 3, label: 'Child Tables' },
  { id: 4, label: 'Source Filters' },
  ...(AI_SAMPLING_ENABLED ? [{ id: 5, label: 'AI Sampling' }] : []),
  { id: 6, label: 'Target' },
  { id: 7, label: 'Field Mapping' },
  { id: 8, label: 'Dependencies' },
  { id: 9, label: 'Validation' },
] as const;

type StepId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

// ---------------------------------------------------------------------------
// Wizard state shape
// ---------------------------------------------------------------------------

interface WizardState {
  // Step 1
  displayName: string;
  key: string;
  description: string;
  kind: 'system-known' | 'user-defined';
  adapterKey: string;

  // Step 2
  primaryTableName: string;
  primaryTableSchema: string;
  primaryKey: string;
  primaryColumns: string[];
  tenantScopeColumn: string;

  // Step 3
  childTables: Array<{
    name: string;
    schema: string;
    primaryKey: string;
    columns: string[];
    filters: SqlFilter[];
  }>;
  relationships: SqlRelationship[];

  // Step 4
  primaryFilters: SqlFilter[];

  // Step 5 (AI enrichment — Pass 1 + Pass 2)
  sampleSize: number;
  sampleScopeType: 'random' | 'department';
  sampleDeptId?: number;
  /** Legacy department ID for Pass 1 enrichment (required when AI enrichment is used). */
  enrichDeptId?: number;

  // Step 6
  targetCollectionNames: string[];

  // Step 7
  fieldMappings: FieldMapping[];

  // Step 8
  childAdapterKeys: string[];
}

function emptyWizardState(): WizardState {
  return {
    displayName: '',
    key: '',
    description: '',
    kind: 'user-defined',
    adapterKey: '',
    primaryTableName: '',
    primaryTableSchema: 'dbo',
    primaryKey: '',
    primaryColumns: [],
    tenantScopeColumn: '',
    childTables: [],
    relationships: [],
    primaryFilters: [{ column: 'is_icc', operator: '=', value: 0 }], // ICC exclusion filter — always locked
    sampleSize: 200,
    sampleScopeType: 'random',
    sampleDeptId: undefined,
    targetCollectionNames: [],
    fieldMappings: [],
    childAdapterKeys: [],
  };
}

// ---------------------------------------------------------------------------
// Slug helper
// ---------------------------------------------------------------------------

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract DatasetBuilderWizardProps
 *
 * Purpose: Props for the DatasetBuilderWizard modal.
 * Fields:
 * - editId: When provided, wizard opens in edit mode for the given template _id.
 * - onClose: Called when the wizard is dismissed (after save or explicit cancel).
 * Rules:
 * - editId null → create mode; editId non-null → edit mode (draft only).
 * Sync: mutations go through useDatasetTemplates.
 */
export interface DatasetBuilderWizardProps {
  editId: string | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Step progress bar
// ---------------------------------------------------------------------------

function StepProgressBar({ currentStep, steps }: { currentStep: number; steps: readonly { id: number; label: string }[] }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      {steps.map((step, i) => (
        <React.Fragment key={step.id}>
          <div className={`flex items-center gap-1.5 shrink-0 ${currentStep === step.id ? 'opacity-100' : 'opacity-50'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              currentStep > step.id
                ? 'bg-green-500 text-white'
                : currentStep === step.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {currentStep > step.id ? <CheckCircle2 className="h-3 w-3" /> : step.id}
            </span>
            <span className={`text-xs whitespace-nowrap ${currentStep === step.id ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-px w-4 shrink-0 ${currentStep > step.id ? 'bg-green-400' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ICC filter locked row
// ---------------------------------------------------------------------------

function LockedFilterRow({ filter }: { filter: SqlFilter }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg opacity-75">
      <span className="text-xs font-mono text-gray-700">
        {filter.column} {filter.operator} {String(filter.value)}
      </span>
      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-medium">locked</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard component
// ---------------------------------------------------------------------------

/**
 * @component DatasetBuilderWizard
 * Purpose:     Full-screen modal wizard for creating or editing a migration dataset template.
 * Props:       DatasetBuilderWizardProps
 * Behavior:    9-step flow; each step validates before advancing; saves draft at any exit.
 * Data Source: local (Dexie for manifest tables; mutations via useDatasetTemplates)
 * Constraints: no direct API calls; no fetch in useEffect
 * Performance: schema manifest loaded once on mount; wizard state is local React state
 */
export function DatasetBuilderWizard({ editId, onClose }: DatasetBuilderWizardProps) {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [wizardState, setWizardState] = useState<WizardState>(emptyWizardState());
  const [stepError, setStepError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  /** Column profiles returned by Pass 1 (stored locally for display; persisted in aiMetadata on server). */
  const [enrichedProfiles, setEnrichedProfiles] = useState<ColumnProfile[]>([]);
  /** AI mapping suggestions returned by Pass 2 (stored locally until user accepts them). */
  const [aiSuggestedMappings, setAiSuggestedMappings] = useState<SuggestedMapping[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Template ID once created (used for subsequent updates)
  const [draftId, setDraftId] = useState<string | null>(editId);

  // Validation issues from Step 9 (server-side validate response)
  const [validationIssues, setValidationIssues] = useState<string[]>([]);

  // Schema manifest tables (loaded once on mount from Dexie)
  const [manifestTables, setManifestTables] = useState<SchemaManifestTableEntry[]>([]);

  const { create, update, activate } = useDatasetTemplates();

  // Active-template list for dependency checker
  const { templates: allTemplates } = useDatasetTemplates('active');

  // ---------------------------------------------------------------------------
  // Load manifest tables on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    void (async () => {
      // We don't know which legacyDb to use from the wizard — use the first populated manifest
      // by iterating over known legacy database names. In practice, one manifest is expected.
      for (const legacyDb of ['SLInspectionsDB001', 'SLInspectionsDB002']) {
        try {
          const manifest = await getSchemaManifest(legacyDb);
          if (manifest && manifest.tables.length > 0) {
            setManifestTables(manifest.tables.filter((t) => !t.disabled));
            break;
          }
        } catch {
          // continue to next db
        }
      }
    })();
  }, []);

  // ---------------------------------------------------------------------------
  // Load existing template in edit mode
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!editId) return;
    void (async () => {
      const template = await getDatasetTemplate(editId);
      if (!template) return;
      setWizardState({
        displayName: template.displayName,
        key: template.key,
        description: template.description,
        kind: template.kind,
        adapterKey: template.key,
        primaryTableName: template.source.primaryTable.name,
        primaryTableSchema: template.source.primaryTable.schema ?? 'dbo',
        primaryKey: template.source.primaryTable.primaryKey,
        primaryColumns: template.source.primaryTable.columns as string[],
        tenantScopeColumn: template.source.tenantScopeColumn,
        childTables: template.source.childTables.map((t) => ({
          name: t.name,
          schema: t.schema ?? 'dbo',
          primaryKey: t.primaryKey,
          columns: t.columns as string[],
          filters: t.filters ?? [],
        })),
        relationships: template.source.relationships ?? [],
        primaryFilters: template.source.filters ?? [{ column: 'is_icc', operator: '=', value: 0 }],
        sampleSize: template.aiMetadata?.sampleSize ?? 200,
        sampleScopeType: template.aiMetadata?.sampleScopeType ?? 'random',
        sampleDeptId: template.aiMetadata?.sampleDeptId,
        enrichDeptId: template.aiMetadata?.enrichDeptId,
        targetCollectionNames: template.target.collections.map((c) => c.name),
        fieldMappings: template.fieldMappings ?? [],
        childAdapterKeys: template.composite?.childAdapterKeys ?? [],
      });
      // Restore locally-displayed enriched profiles from previously-saved aiMetadata
      if (template.aiMetadata?.columnProfiles?.length) {
        setEnrichedProfiles(template.aiMetadata.columnProfiles);
      }
      // Restore locally-displayed AI mapping suggestions from previously-saved aiMetadata
      if (template.aiMetadata?.suggestedMappings?.length) {
        setAiSuggestedMappings(template.aiMetadata.suggestedMappings);
      }
    })();
  }, [editId]);

  // ---------------------------------------------------------------------------
  // Derived helpers
  // ---------------------------------------------------------------------------

  const activeSteps = STEPS;

  const totalSteps = activeSteps.length;

  const visibleStepIndex = activeSteps.findIndex((s) => s.id === currentStep);

  const targetCollectionDefs = useMemo<TargetCollectionFields[]>(
    () =>
      wizardState.targetCollectionNames
        .map((name) => KNOWN_COLLECTIONS.find((c) => c.name === name))
        .filter(Boolean)
        .map((c) => ({
          collectionName: c!.name,
          requiredFields: c!.requiredFields,
          nullableFields: c!.nullableFields,
        })),
    [wizardState.targetCollectionNames],
  );

  const sourceTables = useMemo<SourceTableColumns[]>(() => {
    const result: SourceTableColumns[] = [];
    if (wizardState.primaryTableName) {
      const manifestEntry = manifestTables.find((t) => t.table === wizardState.primaryTableName);
      result.push({
        tableName: `${wizardState.primaryTableSchema}.${wizardState.primaryTableName}`,
        columns: manifestEntry ? toColumnNames(manifestEntry.columns) : wizardState.primaryColumns,
      });
    }
    wizardState.childTables.forEach((ct) => {
      const manifestEntry = manifestTables.find((t) => t.table === ct.name);
      result.push({
        tableName: `${ct.schema}.${ct.name}`,
        columns: manifestEntry ? toColumnNames(manifestEntry.columns) : ct.columns,
      });
    });
    return result;
  }, [wizardState.primaryTableName, wizardState.primaryTableSchema, wizardState.primaryColumns, wizardState.childTables, manifestTables]);

  const selectedPrimaryManifestEntry = useMemo(
    () => manifestTables.find((t) => t.table === wizardState.primaryTableName) ?? null,
    [manifestTables, wizardState.primaryTableName],
  );

  const primaryAvailableColumns = useMemo(() => toColumnNames(selectedPrimaryManifestEntry?.columns), [selectedPrimaryManifestEntry]);

  const primarySelectedColumnSet = useMemo(() => new Set(wizardState.primaryColumns), [wizardState.primaryColumns]);

  // ---------------------------------------------------------------------------
  // Step validation
  // ---------------------------------------------------------------------------

  function validateCurrentStep(): string | null {
    switch (currentStep) {
      case 1:
        if (!wizardState.displayName.trim()) return 'Display name is required.';
        if (!wizardState.key.trim()) return 'Key is required.';
        return null;
      case 2:
        if (!wizardState.primaryTableName) return 'Select a primary source table.';
        if (!wizardState.primaryKey) return 'Select the primary key column for the primary source table.';
        if (!wizardState.tenantScopeColumn) return 'Tenant scope column is required.';
        if (!wizardState.primaryColumns.includes(wizardState.primaryKey)) return 'Primary key must be included in the source table column list.';
        if (!wizardState.primaryColumns.includes(wizardState.tenantScopeColumn)) return 'Tenant scope column must be included in the source table column list.';
        return null;
      case 3:
        // Orphan policy validation
        for (const rel of wizardState.relationships) {
          if (rel.orphanPolicy === 'warn' && !rel.orphanJustification?.trim()) {
            return `Relationship ${rel.parentTable}.${rel.parentColumn} → ${rel.childTable}.${rel.childColumn} has orphan policy "warn" but no justification. Add a justification or change to "block".`;
          }
        }
        return null;
      case 4:
        return null;
      case 5:
        return null; // AI sampling is optional (skip allowed)
      case 6:
        if (wizardState.targetCollectionNames.length === 0) return 'Select at least one target collection.';
        return null;
      case 7: {
        // Check for unmapped required fields
        const unmapped: string[] = [];
        for (const col of targetCollectionDefs) {
          for (const field of col.requiredFields) {
            const isMapped = wizardState.fieldMappings.some(
              (m) => m.targetCollection === col.collectionName && m.targetField === field,
            );
            if (!isMapped) unmapped.push(`${col.collectionName}.${field}`);
          }
        }
        if (unmapped.length > 0) {
          return `Unmapped required fields: ${unmapped.join(', ')}. Map all required fields before advancing.`;
        }
        return null;
      }
      case 8: {
        const unresolvedDeps = wizardState.childAdapterKeys.filter(
          (key) => !allTemplates.some((t) => t.key === key && t.status === 'active'),
        );
        if (unresolvedDeps.length > 0) {
          return `Unresolved dependencies: ${unresolvedDeps.join(', ')}. These must be active templates before saving.`;
        }
        return null;
      }
      case 9:
        return null;
      default:
        return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Build template payload from wizard state
  // ---------------------------------------------------------------------------

  function buildTemplatePayload(): Parameters<typeof create>[0] {
    const primaryManifestEntry = manifestTables.find((t) => t.table === wizardState.primaryTableName);

    return {
      key: wizardState.kind === 'system-known' ? wizardState.adapterKey : wizardState.key,
      displayName: wizardState.displayName,
      description: wizardState.description,
      kind: wizardState.kind,
      source: {
        primaryTable: {
          name: wizardState.primaryTableName,
          schema: wizardState.primaryTableSchema,
          primaryKey: wizardState.primaryKey,
          columns: wizardState.primaryColumns.length > 0
            ? wizardState.primaryColumns
            : toColumnNames(primaryManifestEntry?.columns),
          filters: [],
        },
        childTables: wizardState.childTables.map((ct) => ({
          name: ct.name,
          schema: ct.schema,
          primaryKey: ct.primaryKey,
          columns: ct.columns,
          filters: ct.filters,
        })),
        relationships: wizardState.relationships,
        filters: wizardState.primaryFilters,
        tenantScopeColumn: wizardState.tenantScopeColumn,
      },
      target: {
        collections: wizardState.targetCollectionNames.map((name) => ({
          name,
          provenanceField: 'legacyId',
        })),
      },
      fieldMappings: wizardState.fieldMappings,
      composite: wizardState.childAdapterKeys.length > 0
        ? { childAdapterKeys: wizardState.childAdapterKeys }
        : undefined,
    };
  }

  // ---------------------------------------------------------------------------
  // Save as draft
  // ---------------------------------------------------------------------------

  const handleSaveDraft = useCallback(async (): Promise<string | null> => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      let resultId: string | null = draftId;
      const payload = buildTemplatePayload();
      if (draftId) {
        await update(draftId, payload);
      } else {
        const created = await create(payload);
        if (created) {
          setDraftId(created._id);
          resultId = created._id;
        }
      }
      setIsDirty(false);
      return resultId;
    } catch (err) {
      console.error('[DatasetBuilderWizard] save draft failed:', err);
      setGlobalError(err instanceof Error ? err.message : 'Failed to save draft.');
      return null;
    } finally {
      setIsSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, wizardState, create, update]);

  // ---------------------------------------------------------------------------
  // Activate
  // ---------------------------------------------------------------------------

  const handleActivate = useCallback(async () => {
    const savedId = await handleSaveDraft();
    if (!savedId) return;
    setIsSaving(true);
    setGlobalError(null);
    try {
      const result = await activate(savedId);
      if (result) {
        onClose();
      }
    } catch (err) {
      console.error('[DatasetBuilderWizard] activate failed:', err);
      const message = err instanceof Error ? err.message : 'Activation failed.';
      // Try to parse issues out of the response
      try {
        const parsed = JSON.parse(message);
        if (Array.isArray(parsed.issues)) {
          setValidationIssues(parsed.issues.map((i: { message: string }) => i.message));
          return;
        }
      } catch { /* not JSON */ }
      setGlobalError(message);
    } finally {
      setIsSaving(false);
    }
  }, [handleSaveDraft, activate, onClose]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const handleNext = useCallback(() => {
    const error = validateCurrentStep();
    if (error) {
      setStepError(error);
      return;
    }
    setStepError(null);
    const nextVisibleIndex = visibleStepIndex + 1;
    if (nextVisibleIndex < activeSteps.length) {
      setCurrentStep(activeSteps[nextVisibleIndex].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, wizardState, targetCollectionDefs, allTemplates, visibleStepIndex, activeSteps]);

  const handleBack = useCallback(() => {
    setStepError(null);
    const prevVisibleIndex = visibleStepIndex - 1;
    if (prevVisibleIndex >= 0) {
      setCurrentStep(activeSteps[prevVisibleIndex].id);
    }
  }, [visibleStepIndex, activeSteps]);

  const handleClose = useCallback(async () => {
    if (isDirty) {
      await handleSaveDraft();
    }
    onClose();
  }, [isDirty, handleSaveDraft, onClose]);

  const setField = <K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setWizardState((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  // ---------------------------------------------------------------------------
  // AI Enrichment — Pass 1 (column profiling)
  // ---------------------------------------------------------------------------

  /**
   * @process handleEnrich
   * Steps:   1. Save as draft if no draftId yet. 2. POST /:id/enrich with deptId + sampleSize.
   *          3. Store returned columnProfiles in local state for display.
   * Inputs:  wizardState.enrichDeptId, wizardState.sampleSize
   * Outputs: enrichedProfiles state updated; template.aiMetadata.enrichedAt set on server.
   * Failure Handling: globalError banner shown; enrichedProfiles unchanged.
   * Idempotency: REQUIRED — can be re-run at any time while template is draft.
   */
  const handleEnrich = useCallback(async () => {
    if (!wizardState.enrichDeptId) {
      setGlobalError('A legacy department ID is required for AI column enrichment.');
      return;
    }
    let id = draftId;
    if (!id) {
      const savedId = await handleSaveDraft();
      if (!savedId) return;
      id = savedId;
    }
    setIsEnriching(true);
    setGlobalError(null);
    try {
      const updated = await enrichDatasetTemplate(id, {
        deptId: wizardState.enrichDeptId,
        sampleSize: wizardState.sampleSize,
      });
      const profiles = updated.aiMetadata?.columnProfiles ?? [];
      setEnrichedProfiles(profiles);
    } catch (err) {
      console.error('[DatasetBuilderWizard] enrichment failed:', err);
      setGlobalError(err instanceof Error ? err.message : 'AI column enrichment failed.');
    } finally {
      setIsEnriching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, wizardState.enrichDeptId, wizardState.sampleSize, handleSaveDraft]);

  // ---------------------------------------------------------------------------
  // AI Field Matching — Pass 2 (mapping suggestions)
  // ---------------------------------------------------------------------------

  /**
   * @process handleMatch
   * Steps:   1. Save as draft if no draftId yet. 2. POST /:id/match (no body).
   *          3. Store returned suggestedMappings in local state.
   * Inputs:  draftId (Pass 1 must have run first — server enforces this with 400).
   * Outputs: aiSuggestedMappings state updated; template.aiMetadata.matchedAt set on server.
   * Failure Handling: globalError banner shown; aiSuggestedMappings unchanged.
   * Idempotency: REQUIRED — can be re-run at any time while template is draft.
   */
  const handleMatch = useCallback(async () => {
    let id = draftId;
    if (!id) {
      const savedId = await handleSaveDraft();
      if (!savedId) return;
      id = savedId;
    }
    setIsMatching(true);
    setGlobalError(null);
    try {
      const updated = await matchDatasetTemplate(id);
      const suggestions = updated.aiMetadata?.suggestedMappings ?? [];
      setAiSuggestedMappings(suggestions);
    } catch (err) {
      console.error('[DatasetBuilderWizard] mapping match failed:', err);
      setGlobalError(err instanceof Error ? err.message : 'AI mapping match failed.');
    } finally {
      setIsMatching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, handleSaveDraft]);

  /**
   * @process handleAcceptAiMappings
   * Steps:   Merge AI suggestions into current fieldMappings, skipping duplicates
   *          (same sourceTable + sourceColumn + targetCollection + targetField).
   * Inputs:  aiSuggestedMappings, wizardState.fieldMappings
   * Outputs: wizardState.fieldMappings extended with non-duplicate suggestions.
   * Idempotency: REQUIRED — re-clicking is safe (duplicates are silently skipped).
   */
  const handleAcceptAiMappings = useCallback(() => {
    const existing = new Set(
      wizardState.fieldMappings.map(
        (m) => `${m.sourceTable}::${m.sourceColumn}::${m.targetCollection}::${m.targetField}`,
      ),
    );
    const newMappings: FieldMapping[] = aiSuggestedMappings
      .filter(
        (s) =>
          !existing.has(
            `${s.sourceTable}::${s.sourceColumn}::${s.targetCollection}::${s.targetField}`,
          ),
      )
      .map((s) => ({
        sourceTable: s.sourceTable,
        sourceColumn: s.sourceColumn,
        targetCollection: s.targetCollection,
        targetField: s.targetField,
      }));
    if (newMappings.length > 0) {
      setField('fieldMappings', [...wizardState.fieldMappings, ...newMappings]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSuggestedMappings, wizardState.fieldMappings]);

  // ---------------------------------------------------------------------------
  // Render step content
  // ---------------------------------------------------------------------------

  function renderStepContent() {
    switch (currentStep) {
      // -----------------------------------------------------------------------
      // Step 1 — Name & Purpose
      // -----------------------------------------------------------------------
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={wizardState.displayName}
                onChange={(e) => {
                  const name = e.target.value;
                  setField('displayName', name);
                  if (!draftId) setField('key', toSlug(name));
                }}
                placeholder="e.g. Violation Code Sets"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Key <span className="text-red-500">*</span>
                <span className="ml-1 text-gray-400 font-normal">(URL-safe slug)</span>
              </label>
              <input
                type="text"
                value={wizardState.key}
                onChange={(e) => setField('key', e.target.value)}
                placeholder="e.g. violation-code-sets"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={wizardState.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={3}
                placeholder="What data does this dataset migrate and why?"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Kind</label>
              <div className="flex gap-3">
                {(['user-defined', 'system-known'] as const).map((kind) => (
                  <label key={kind} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm transition-colors ${
                    wizardState.kind === kind
                      ? 'border-orange-400 bg-orange-50 text-orange-800'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="kind"
                      value={kind}
                      checked={wizardState.kind === kind}
                      onChange={() => {
                        setField('kind', kind);
                        if (kind === 'system-known') {
                          const adapter = REGISTERED_ADAPTERS_MANIFEST[0];
                          if (adapter) {
                            setField('adapterKey', adapter.key);
                            setField('key', adapter.key);
                            setField('displayName', adapter.displayName);
                          }
                        }
                      }}
                      className="sr-only"
                    />
                    <span className="capitalize">{kind === 'user-defined' ? 'User-defined (Custom)' : 'System-known'}</span>
                  </label>
                ))}
              </div>
            </div>
            {wizardState.kind === 'system-known' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Adapter</label>
                <select
                  value={wizardState.adapterKey}
                  onChange={(e) => {
                    const adapter = REGISTERED_ADAPTERS_MANIFEST.find((a) => a.key === e.target.value);
                    if (adapter) {
                      setField('adapterKey', adapter.key);
                      setField('key', adapter.key);
                      setField('displayName', adapter.displayName);
                      setField('targetCollectionNames', adapter.targetCollections);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="">Select adapter…</option>
                  {REGISTERED_ADAPTERS_MANIFEST.map((a) => (
                    <option key={a.key} value={a.key}>{a.displayName}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 2 — Primary Source Table
      // -----------------------------------------------------------------------
      case 2: {
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Primary Source Table <span className="text-red-500">*</span>
              </label>
              <select
                value={wizardState.primaryTableName}
                onChange={(e) => {
                  const entry = manifestTables.find((t) => t.table === e.target.value);
                  const columns = toColumnNames(entry?.columns);
                  setField('primaryTableName', e.target.value);
                  setField('primaryTableSchema', entry?.schema ?? 'dbo');
                  setField('primaryKey', entry?.primaryKey ?? columns.find((col) => col.toLowerCase().endsWith('id')) ?? '');
                  setField('primaryColumns', columns);
                  setField('tenantScopeColumn', '');
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="">Select table…</option>
                {manifestTables.map((t) => (
                  <option key={t.table} value={t.table}>
                    {t.schema}.{t.table}
                  </option>
                ))}
              </select>
            </div>
            {primaryAvailableColumns.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Primary Key <span className="text-red-500">*</span>
                </label>
                <select
                  value={wizardState.primaryKey}
                  onChange={(e) => setField('primaryKey', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4"
                >
                  <option value="">Select primary key…</option>
                  {primaryAvailableColumns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tenant Scope Column <span className="text-red-500">*</span>
                  <span className="ml-1 text-gray-400 font-normal">(column used to filter rows by department)</span>
                </label>
                <select
                  value={wizardState.tenantScopeColumn}
                  onChange={(e) => setField('tenantScopeColumn', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="">Select column…</option>
                  {primaryAvailableColumns.filter((col) => wizardState.primaryColumns.includes(col)).map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            )}
            {primaryAvailableColumns.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Columns ({primaryAvailableColumns.length})
                </p>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {primaryAvailableColumns.map((col) => (
                    <label key={col} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={primarySelectedColumnSet.has(col)}
                        disabled={col === wizardState.primaryKey}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? Array.from(new Set([...wizardState.primaryColumns, col]))
                            : wizardState.primaryColumns.filter((c) => c !== col);
                          setField('primaryColumns', next);
                          if (!next.includes(wizardState.tenantScopeColumn)) {
                            setField('tenantScopeColumn', '');
                          }
                        }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-400"
                      />
                      <span className="font-mono text-gray-700">{col}</span>
                      {col === wizardState.primaryKey && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold">PK</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {manifestTables.length === 0 && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                No schema manifest loaded. Populate the Schema Manifest first.
              </div>
            )}
          </div>
        );
      }

      // -----------------------------------------------------------------------
      // Step 3 — Child Tables & Joins
      // -----------------------------------------------------------------------
      case 3:
        return (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              Add child tables that join to the primary table. Set orphan policy per relationship.
            </p>
            {/* Child tables list */}
            {wizardState.childTables.length > 0 && (
              <div className="space-y-2">
                {wizardState.childTables.map((ct, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <span className="text-xs font-mono text-gray-800 flex-1">{ct.schema}.{ct.name}</span>
                    <button
                      onClick={() => setField('childTables', wizardState.childTables.filter((_, idx) => idx !== i))}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Add child table selector */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Add Child Table</label>
                <select
                  onChange={(e) => {
                    const entry = manifestTables.find((t) => t.table === e.target.value);
                    if (!entry) return;
                    if (wizardState.childTables.some((ct) => ct.name === entry.table)) return;
                    const columns = toColumnNames(entry.columns);
                    const nextChildTables: WizardState['childTables'] = [
                      ...wizardState.childTables,
                      {
                        name: entry.table,
                        schema: entry.schema ?? 'dbo',
                        primaryKey: entry.primaryKey ?? columns.find((col) => col.toLowerCase().endsWith('id')) ?? '',
                        columns,
                        filters: [] as SqlFilter[],
                      },
                    ];
                    setField('childTables', nextChildTables);
                    e.target.value = '';
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  defaultValue=""
                >
                  <option value="">Select table to add…</option>
                  {manifestTables
                    .filter((t) => t.table !== wizardState.primaryTableName && !wizardState.childTables.some((ct) => ct.name === t.table))
                    .map((t) => (
                      <option key={t.table} value={t.table}>{t.schema}.{t.table}</option>
                    ))}
                </select>
              </div>
            </div>
            {/* Relationships */}
            {wizardState.relationships.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-2">Relationships</h4>
                <div className="space-y-2">
                  {wizardState.relationships.map((rel, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg px-3 py-2.5 space-y-2">
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <span>{rel.parentTable}.{rel.parentColumn}</span>
                        <span className="text-gray-400">→</span>
                        <span>{rel.childTable}.{rel.childColumn}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-gray-600">Orphan Policy:</label>
                        <div className="flex gap-2">
                          {(['block', 'warn'] as const).map((policy) => (
                            <label key={policy} className={`flex items-center gap-1 px-2 py-1 border rounded text-xs cursor-pointer ${
                              rel.orphanPolicy === policy ? 'border-orange-400 bg-orange-50 text-orange-800' : 'border-gray-200 text-gray-700'
                            }`}>
                              <input
                                type="radio"
                                name={`orphan-${i}`}
                                checked={rel.orphanPolicy === policy}
                                onChange={() => {
                                  const updated = [...wizardState.relationships];
                                  updated[i] = { ...updated[i], orphanPolicy: policy };
                                  setField('relationships', updated);
                                }}
                                className="sr-only"
                              />
                              {policy}
                            </label>
                          ))}
                        </div>
                      </div>
                      {rel.orphanPolicy === 'warn' && (
                        <input
                          type="text"
                          placeholder="Justification for warn policy (required)…"
                          value={rel.orphanJustification ?? ''}
                          onChange={(e) => {
                            const updated = [...wizardState.relationships];
                            updated[i] = { ...updated[i], orphanJustification: e.target.value };
                            setField('relationships', updated);
                          }}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        />
                      )}
                      <button
                        onClick={() => setField('relationships', wizardState.relationships.filter((_, idx) => idx !== i))}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 4 — Source Filters
      // -----------------------------------------------------------------------
      case 4:
        return (
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              Filters applied to the primary table SQL query. The ICC/NFPA exclusion filter is locked and always applied.
            </p>
            {/* Locked filters */}
            <div className="space-y-1">
              {wizardState.primaryFilters.slice(0, 1).map((f, i) => (
                <LockedFilterRow key={i} filter={f} />
              ))}
            </div>
            {/* User-defined additional filters */}
            {wizardState.primaryFilters.slice(1).map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={f.column}
                  onChange={(e) => {
                    const updated = [...wizardState.primaryFilters];
                    updated[i + 1] = { ...updated[i + 1], column: e.target.value };
                    setField('primaryFilters', updated);
                  }}
                  placeholder="column"
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono"
                />
                <select
                  value={f.operator}
                  onChange={(e) => {
                    const updated = [...wizardState.primaryFilters];
                    updated[i + 1] = { ...updated[i + 1], operator: e.target.value as SqlFilter['operator'] };
                    setField('primaryFilters', updated);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                >
                  {['=', '!=', '<', '>', '<=', '>=', 'IS NULL', 'IS NOT NULL'].map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={String(f.value ?? '')}
                  onChange={(e) => {
                    const updated = [...wizardState.primaryFilters];
                    updated[i + 1] = { ...updated[i + 1], value: e.target.value };
                    setField('primaryFilters', updated);
                  }}
                  placeholder="value"
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono"
                />
                <button
                  onClick={() => setField('primaryFilters', wizardState.primaryFilters.filter((_, idx) => idx !== i + 1))}
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setField('primaryFilters', [
                  ...wizardState.primaryFilters,
                  { column: '', operator: '=', value: '' },
                ])
              }
              className="inline-flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-800 font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              Add filter
            </button>
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 5 — AI Column Enrichment (Pass 1) — only when AI_SAMPLING_ENABLED
      // -----------------------------------------------------------------------
      case 5:
        return (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              Runs GPT-assisted column profiling against a live sample of source data for the
              selected department. Raw rows are never stored — only AI analysis results are saved.
              Run this step before proceeding to Field Mapping to enable AI mapping suggestions.
            </p>

            {/* Department ID (required for Pass 1) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Legacy Department ID <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={wizardState.enrichDeptId ?? ''}
                onChange={(e) => setField('enrichDeptId', Number(e.target.value) || undefined)}
                placeholder="e.g. 42"
                className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Sample size */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Sample Size
                <span className="ml-1 text-gray-400 font-normal">(rows per table, max 200)</span>
              </label>
              <div className="flex gap-2 items-center">
                {[25, 50, 100].map((size) => (
                  <button
                    key={size}
                    onClick={() => setField('sampleSize', size)}
                    className={`px-3 py-1.5 text-xs border rounded-lg font-medium transition-colors ${
                      wizardState.sampleSize === size
                        ? 'border-orange-400 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={wizardState.sampleSize}
                  onChange={(e) => setField('sampleSize', Math.min(200, Math.max(1, Number(e.target.value))))}
                  className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-xs"
                />
              </div>
            </div>

            {/* Enrich button */}
            <button
              onClick={() => void handleEnrich()}
              disabled={isEnriching || !wizardState.enrichDeptId}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isEnriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {isEnriching ? 'Enriching columns…' : enrichedProfiles.length > 0 ? 'Re-run Enrichment' : 'Run AI Enrichment'}
            </button>
            {!wizardState.enrichDeptId && (
              <p className="text-xs text-amber-600">Enter a legacy department ID above to enable enrichment.</p>
            )}

            {/* Enriched column profiles table */}
            {enrichedProfiles.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-700">
                    Enriched Column Profiles
                    <span className="ml-1.5 text-gray-400 font-normal">({enrichedProfiles.length} columns)</span>
                  </h4>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Table</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Column</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Semantic Label</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Classification</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Type</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-600 border-b border-gray-200">Null%</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">Distribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichedProfiles.map((profile, idx) => (
                        <tr
                          key={`${profile.sourceTable}::${profile.sourceColumn}`}
                          className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-3 py-2 font-mono text-gray-500">{profile.sourceTable}</td>
                          <td className="px-3 py-2 font-mono font-medium text-gray-800">{profile.sourceColumn}</td>
                          <td className="px-3 py-2 text-gray-700">{profile.semanticLabel ?? <span className="text-gray-400 italic">—</span>}</td>
                          <td className="px-3 py-2">
                            {profile.dataClassification ? (
                              <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                {profile.dataClassification}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-600">{profile.inferredType}</td>
                          <td className="px-3 py-2 text-right text-gray-600">
                            {(profile.nullRate * 100).toFixed(0)}%
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={profile.distributionSummary}>
                            {profile.distributionSummary ?? <span className="text-gray-400 italic">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {enrichedProfiles.some((p) => p.qualityFlags?.length > 0) && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      Some columns have quality flags detected by the AI. Review before activating.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 6 — Target Collections
      // -----------------------------------------------------------------------
      case 6:
        return (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              Select the FireSight MongoDB collections this dataset populates.
              Only registered collections are allowed.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {KNOWN_COLLECTIONS.map((col: KnownCollectionEntry) => {
                const isSelected = wizardState.targetCollectionNames.includes(col.name);
                return (
                  <label
                    key={col.name}
                    className={`flex items-start gap-3 px-3 py-2.5 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const names = e.target.checked
                          ? [...wizardState.targetCollectionNames, col.name]
                          : wizardState.targetCollectionNames.filter((n) => n !== col.name);
                        setField('targetCollectionNames', names);
                      }}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-xs font-semibold text-gray-800">{col.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Required: {col.requiredFields.join(', ')}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 7 — Field Mapping
      // -----------------------------------------------------------------------
      case 7:
        return (
          <div className="space-y-4">
            {/* AI suggestions toolbar — only rendered when Pass 1 has run */}
            {AI_SAMPLING_ENABLED && (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex-1 min-w-0">
                  {aiSuggestedMappings.length > 0 ? (
                    <p className="text-xs text-blue-800">
                      <strong>{aiSuggestedMappings.length} AI mapping suggestion{aiSuggestedMappings.length !== 1 ? 's' : ''}</strong> available from Pass 1 enrichment.
                    </p>
                  ) : (
                    <p className="text-xs text-blue-700">
                      {enrichedProfiles.length > 0
                        ? 'Column profiles ready. Run AI match to generate field mapping suggestions.'
                        : 'Run AI Enrichment (Step 5) first to enable mapping suggestions.'}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {enrichedProfiles.length > 0 && (
                    <button
                      onClick={() => void handleMatch()}
                      disabled={isMatching}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      {isMatching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      {isMatching ? 'Matching…' : 'Run AI Match'}
                    </button>
                  )}
                  {aiSuggestedMappings.length > 0 && (
                    <button
                      onClick={handleAcceptAiMappings}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Accept suggestions
                    </button>
                  )}
                </div>
              </div>
            )}
            <FieldMappingEditor
              fieldMappings={wizardState.fieldMappings}
              onMappingsChange={(mappings) => setField('fieldMappings', mappings)}
              sourceTables={sourceTables}
              targetCollections={targetCollectionDefs}
            />
          </div>
        );

      // -----------------------------------------------------------------------
      // Step 8 — Dependency Declaration
      // -----------------------------------------------------------------------
      case 8: {
        const activeAdapterKeys = allTemplates.map((t) => t.key);
        const unresolved = wizardState.childAdapterKeys.filter((k) => !activeAdapterKeys.includes(k));
        return (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              Declare any dataset templates this dataset depends on. All declared dependencies
              must be active templates before this dataset can be activated.
            </p>
            {wizardState.childAdapterKeys.length > 0 && (
              <div className="space-y-2">
                {wizardState.childAdapterKeys.map((key, i) => {
                  const isActive = activeAdapterKeys.includes(key);
                  return (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                      <span className="text-xs font-mono text-gray-800 flex-1">{key}</span>
                      {isActive ? (
                        <span className="text-xs text-green-700 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Active
                        </span>
                      ) : (
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Not active
                        </span>
                      )}
                      <button
                        onClick={() => setField('childAdapterKeys', wizardState.childAdapterKeys.filter((_, idx) => idx !== i))}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {unresolved.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Unresolved dependencies: <strong>{unresolved.join(', ')}</strong>.
                  These must be active before this dataset can be activated.
                </span>
              </div>
            )}
            <div className="flex gap-2">
              <select
                onChange={(e) => {
                  const key = e.target.value;
                  if (!key || wizardState.childAdapterKeys.includes(key)) return;
                  setField('childAdapterKeys', [...wizardState.childAdapterKeys, key]);
                  e.target.value = '';
                }}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                defaultValue=""
              >
                <option value="">Add dependency…</option>
                {allTemplates
                  .filter((t) => !wizardState.childAdapterKeys.includes(t.key))
                  .map((t) => (
                    <option key={t.key} value={t.key}>{t.displayName} ({t.key})</option>
                  ))}
              </select>
            </div>
          </div>
        );
      }

      // -----------------------------------------------------------------------
      // Step 9 — Validation Summary
      // -----------------------------------------------------------------------
      case 9:
        return (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              Review the validation results. "Save as Draft" is always available.
              "Activate" is available only when all guards pass.
            </p>
            {validationIssues.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  Validation issues ({validationIssues.length})
                </div>
                <ul className="space-y-1">
                  {validationIssues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded px-3 py-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-4 py-3">
                <CheckCircle2 className="h-5 w-5" />
                <span>Template is ready to activate. All validation guards pass.</span>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => void handleSaveDraft()}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save as Draft
              </button>
              <button
                onClick={() => void handleActivate()}
                disabled={isSaving || validationIssues.length > 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Activate
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  const isLastStep = visibleStepIndex === activeSteps.length - 1;
  const isFirstStep = visibleStepIndex === 0;

  // ---------------------------------------------------------------------------
  // Render modal
  // ---------------------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {editId ? 'Edit Dataset Template' : 'New Dataset Template'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Step {visibleStepIndex + 1} of {totalSteps} — {activeSteps[visibleStepIndex]?.label}
            </p>
          </div>
          <button
            onClick={() => void handleClose()}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close wizard"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step progress */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
          <StepProgressBar currentStep={currentStep} steps={activeSteps} />
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {globalError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{globalError}</span>
              <button onClick={() => setGlobalError(null)} className="ml-auto text-red-400 hover:text-red-600 text-xs">Dismiss</button>
            </div>
          )}
          {stepError && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{stepError}</span>
            </div>
          )}
          {renderStepContent()}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 shrink-0">
          <div className="flex gap-2">
            <button
              onClick={handleBack}
              disabled={isFirstStep || isSaving}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void handleSaveDraft()}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save Draft
            </button>
            {!isLastStep && (
              <button
                onClick={handleNext}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
